import { ClientMetadata, CognitoUserAttribute } from 'amazon-cognito-identity-js'
import React, { useState, useEffect, useContext } from 'react'
import axios from 'axios';
import config from '../config.js';
import * as cognito from '../libs/cognito';
import * as cognitoSubApps from '../libs/cognitoSubApps';

export enum AuthStatus {
  Loading,
  SignedIn,
  SignedOut,
}

export interface IAuth {
  sessionInfo?: { username?: string; email?: string; sub?: string; accessToken?: string; refreshToken?: string, idToken?: string }
  attrInfo?: any
  authStatus?: AuthStatus
  signInWithEmail?: any
  //signUpWithEmail?: any
  signOut?: any
  verifyCode?: any
  getSession?: any
  sendCode?: any
  forgotPassword?: any
  changePassword?: any
  getAttributes?: any
  setAttribute?: any
  refreshToken?: any
  requestTokenTOTP?: any
  verifySoftwareTOTP?: any
  enableMFA?: any
  signInWithTOTP?: any
  rememberDevice?: any
  getDevice?: any
  forceForgetDevice?: any
  checkIfUserHasMFA?: any
  signInFromIdpTokensSAML?: any
  logoutInvalidateAllTokens?:any
  getAccessTokenForBE?:any
  updateMissingTokensFromLoggedInSession?:any
}

const defaultState: IAuth = {
  sessionInfo: {},
  authStatus: AuthStatus.Loading,
}

export const AuthContext = React.createContext(defaultState)

export const AuthIsSignedIn: React.FunctionComponent = ({ children }:any) => {
  const { authStatus }: IAuth = useContext(AuthContext)

  return <>{authStatus === AuthStatus.SignedIn ? children : null}</>
}

export const AuthIsNotSignedIn: React.FunctionComponent = ({ children }:any) => {
  const { authStatus }: IAuth = useContext(AuthContext)

  return <>{authStatus === AuthStatus.SignedOut ? children : null}</>
}


let refreshInterval:any = undefined;
let checkSessionInterval:any = undefined;

const AuthProvider: React.FunctionComponent = ({ children }:any) => {
  const [authStatus, setAuthStatus] = useState(AuthStatus.Loading);
  const [sessionInfo, setSessionInfo] = useState({});
  const [attrInfo, setAttrInfo] = useState([]);

  useEffect(() => {
    //console.log('auth context', authStatus)
    async function getSessionInfo() {
      try {
        //da qua passo sempre prima dei componenti, recupero la sessione corrente
        //nel caso non ci sia viene rimandato alla login per generare una sessione
        await checkCurrentSessionIfIsValid(true);

        if(authStatus === 1){
          if(checkSessionInterval === undefined){
            checkSessionInterval = setInterval( async() => {
              checkCurrentSessionIfIsValid();
            }, 60000);
          }
        }

        setAuthStatus(AuthStatus.SignedIn);

      } catch (err) {
        //console.log(err)
        //console.log(err.message.includes('revoked'))
        //config.ENV === 'Dev' && console.log(err)
        if(checkSessionInterval!==undefined){
          clearInterval(checkSessionInterval);
          checkSessionInterval = undefined;
        }
        //se è loggato e va in errore qualcosa invalidazione di sessione
        if(authStatus===1){
          logoutInvalidateAllTokens()
        }else{
          //altrimenti viene spostato nella rotta non autenticata
          setAuthStatus(AuthStatus.SignedOut);
        }
        //sdoppia le logout
        //signOut();
       
      }
    }

    getSessionInfo();

  }, [setAuthStatus, authStatus])


  if (authStatus === AuthStatus.Loading) {
    return null
  }

  async function checkCurrentSessionIfIsValid(fromUseEffect = false){
    try{
      //riconosce quando il refresh è expired
      //riconosce quando il refresh è revoked
      let currentSession: any = await getSession();
      //console.log(currentSession)
      setSessionInfo({
        accessToken: currentSession.getAccessToken().getJwtToken(),
        refreshToken: currentSession.getRefreshToken().getToken(),
        idToken: currentSession.getIdToken().getJwtToken()
      });
      let expTimeAccess = currentSession.getAccessToken().getExpiration(); //unix in seconds
      let secondsFromEpoch = Date.now();
      secondsFromEpoch = secondsFromEpoch / 1000; //into seconds
      let missingSecondsBeforeExpire = expTimeAccess - secondsFromEpoch;
      if (missingSecondsBeforeExpire >= config.GUARD_BEFORE_EXPIRE) {
         //Se c'è una sessione si controlla che l'access non sia revocato
        //in caso l'access sia scaduto viene refreshato
        //usa esclusivamente l'access token
        const attr: any = await getAttributes();
        //console.log(attr)
        setAttrInfo(attr);
      }
    }catch(err){
      //se ho current user a null e non throwo alla use effect questo errore, causo a livello di use effect una login non voluta
      if(fromUseEffect)throw err
      clearInterval(checkSessionInterval);
      checkSessionInterval = undefined;
      if(authStatus===1){
        logoutInvalidateAllTokens()
      }else{
        //altrimenti viene spostato nella rotta non autenticata
        setAuthStatus(AuthStatus.SignedOut);
      }
    }
  }

  async function getAccessTokenForBE(){
    try{
      let refreshSessionResult:any = undefined;
      let currentSession: any = await getSession();
      //check se il token sta per scadere entro i prossimi secondi lato config
      let accessTokenInfoFromSession = await currentSession.getAccessToken()
      let expTimeAccess = await accessTokenInfoFromSession.getExpiration(); //unix in seconds
      let secondsFromEpoch = Date.now();
      secondsFromEpoch = secondsFromEpoch/1000; //into seconds
      let missingSecondsBeforeExpire = expTimeAccess - secondsFromEpoch;
      //console.log('missingSecondsBeforeExpire', missingSecondsBeforeExpire)
      if(missingSecondsBeforeExpire <= config.TOKEN_IS_GOING_EXPIRE){
        //console.log('access token almost expired')
        refreshSessionResult = await cognito.refreshToken(currentSession);
      }
      //console.log('currentSession ', currentSession);
      //console.log('refreshSessionResult ', refreshSessionResult);
      
      setSessionInfo({
        accessToken: refreshSessionResult ? refreshSessionResult.getAccessToken().getJwtToken() : currentSession.getAccessToken().getJwtToken(),
        refreshToken: refreshSessionResult ? refreshSessionResult.getRefreshToken().getToken() : currentSession.getRefreshToken().getToken(),
        idToken: refreshSessionResult ? refreshSessionResult.getIdToken().getJwtToken() : currentSession.getIdToken().getJwtToken()
      });

      return refreshSessionResult ? refreshSessionResult.getAccessToken().getJwtToken() : currentSession.getAccessToken().getJwtToken()
    }catch(err){
      //console.log('getAccessTokenForBE', err);
      clearInterval(checkSessionInterval);
      checkSessionInterval = undefined;
      if(authStatus===1){
        logoutInvalidateAllTokens()
      }else{
        //altrimenti viene spostato nella rotta non autenticata
        setAuthStatus(AuthStatus.SignedOut);
      }
    }
  }

  async function refreshToken() {
    try {
      //rigenerazione dell'access token via refresh token
      let session: any = await getSession();

      await cognito.refreshToken(session);

      session = await getSession();

      setSessionInfo({
        accessToken: session.accessToken.jwtToken,
      });
      
      setAuthStatus(AuthStatus.SignedIn);

    } catch (err) {
      setAuthStatus(AuthStatus.SignedOut);
    }
  }

  async function checkSessionValidity(){
    try{
      //vero test per verificare la validità del refresh token
      const attr: any = await getAttributes();
      setAttrInfo(attr);
    }catch(err){
      signOut();
    }
  }

  async function signInWithEmail(username: string, password: string) {
    try {
      let responseLogin = await cognito.signInWithEmail(username, password);

      if(responseLogin === 'SOFTWARE_TOKEN_MFA'){
        return responseLogin
      }
      //passa con i suppress
      const session: any = await getSession();

      let date = new Date();
      // add a day
      let nextDay:number | Date | string = date.setDate(date.getDate() + 1);
      //set to midnight
      nextDay = date.setHours(0,0,0);

      nextDay = new Date(nextDay).toUTCString();

      /* 
      Errore settare i cookie quando è da configurare l'MFA. In più, non si deve effettuare il redirect alla home dopo la configurazione.
      document.cookie = `hubAccessToken${config.ENV}=${session.getAccessToken().getJwtToken()}; expires=${nextDay}; domain=${config.APPLICATION_DOMAIN}; path=/`;
      document.cookie = `hubRefreshToken${config.ENV}=${session.getRefreshToken().getToken()}; expires=${nextDay}; domain=${config.APPLICATION_DOMAIN}; path=/`;
      document.cookie = `hubIdToken${config.ENV}=${session.getIdToken().getJwtToken()}; expires=${nextDay}; domain=${config.APPLICATION_DOMAIN}; path=/`; */

      setSessionInfo({
        accessToken: session.getAccessToken().getJwtToken(),
        refreshToken: session.getRefreshToken().getToken(),
        idToken: session.getIdToken().getJwtToken()
      });

      setAuthStatus(AuthStatus.SignedIn)
      
      return session.getAccessToken().getJwtToken()

    } catch (err) {
      //console.log('err email login', err);
      setAuthStatus(AuthStatus.SignedOut);
      throw err;
    }
  }

  /* async function signUpWithEmail(username: string, password: string, attributes:CognitoUserAttribute, metaData:ClientMetadata) {
    try {
      await cognito.signUpUserWithEmail(username, password, attributes, metaData);
    } catch (err) {
      throw err
    }
  } */

  async function signOut() {
    localStorage.removeItem('user_email');

    let token:any = sessionInfo;
    
    const params = {};
    const url = `${config.API_URL}/trace-logout`;

    try{
      /* const response = await axios.post(
        url,
        null,
        {
            headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${token.accessToken}`
            },
            params
        })

      if(response){
        //console.log('logout done', response.data);
      }else{
        //console.log('logout error');
        let err = 'logout error';
        throw err;
      } */
    }catch(err){
      //console.log(err);
    }finally{
      clearInterval(checkSessionInterval);
      checkSessionInterval = undefined;
      cognito.signOut();
      localStorage.removeItem('isMFAToConfig');
      setAuthStatus(AuthStatus.SignedOut);
    }
  }

  async function verifyCode(username: string, code: string) {
    try {
      await cognito.verifyCode(username, code)
    } catch (err) {
      throw err
    }
  }

  async function getSession() {
    try {
      const session = await cognito.getSession()
      return session
    } catch (err) {
      throw err
    }
  }

  async function getAttributes() {
    try {
      const attr = await cognito.getAttributes()
      return attr
    } catch (err) {
      throw err
    }
  }

  async function setAttribute(attr: any) {
    try {
      const res = await cognito.setAttribute(attr)
      return res
    } catch (err) {
      throw err
    }
  }

  /*  
  async function sendCode(username: string) {
    try {
      await cognito.sendCode(username)
    } catch (err) {
      throw err
    }
  } */

  async function sendCode(email: string) {
    try {
      await cognito.sendCode(email)
    } catch (err) {
      throw err
    }
  }

  async function forgotPassword(username: string, code: string, password: string) {
    try {
      await cognito.forgotPassword(username, code, password)
    } catch (err) {
      throw err
    }
  }

  async function changePassword(oldPassword: string, newPassword: string) {
    try {
      await cognito.changePassword(oldPassword, newPassword)
    } catch (err) {
      throw err
    }
  }

  async function requestTokenTOTP(){
    try {
      let response = await cognito.requestTokenTOTP()
      return response
    } catch (err) {
      throw err
    }
  }
  
  async function verifySoftwareTOTP(totpCode: string){
    try{
      let response = await cognito.verifySoftwareTOTP(totpCode)
      return response;
    }catch(err){
      throw err
    }
  }

  async function enableMFA() {
    try{
      let response = await cognito.enableMFA()

    //   let token:any = sessionInfo;

    //   const url = `${config.API_URL}/trace-mfa-activation`;

    //   await axios.post(
    //     url,
    //     null,
    //     {
    //         headers: {
    //             'Accept': 'application/json',
    //             'Authorization': `Bearer ${token.accessToken}`
    //         }
    //     }
    //   )

      return response;
    }catch(err){
      throw(err);
    }
  }

  async function signInWithTOTP(totpCode: string, username: string, password:string) {
    try{
      let signInTotpResponse = await cognito.signInWithTOTP(totpCode,username,password)

      const session: any = await getSession();
      let date = new Date();
      // add a day
      let nextDay:number | Date | string = date.setDate(date.getDate() + 1);
      //set to midnight
      nextDay = date.setHours(0,0,0);
  
      nextDay = new Date(nextDay).toUTCString();
  
      document.cookie = `hubAccessToken${config.ENV}=${session.getAccessToken().getJwtToken()}; expires=${nextDay}; domain=${config.APPLICATION_DOMAIN}; path=/`;
      document.cookie = `hubRefreshToken${config.ENV}=${session.getRefreshToken().getToken()}; expires=${nextDay}; domain=${config.APPLICATION_DOMAIN}; path=/`;
      document.cookie = `hubIdToken${config.ENV}=${session.getIdToken().getJwtToken()}; expires=${nextDay}; domain=${config.APPLICATION_DOMAIN}; path=/`;

      setSessionInfo({
        accessToken: session.getAccessToken().getJwtToken(),
        refreshToken: session.getRefreshToken().getToken(),
        idToken: session.getIdToken().getJwtToken()
      });

      setAuthStatus(AuthStatus.SignedIn)

      return session.getAccessToken().getJwtToken();

    }catch(err){
      console.log('err totp login', err);
      setAuthStatus(AuthStatus.SignedOut);
      throw err;
    }
  }

  async function rememberDevice() {
    try{
      let response = await cognito.rememberDevice();
      return response;
    }catch(err){
      throw err;
    }
  }
  
  async function getDevice(){
    return await cognito.getDevice();
  }

  async function forceForgetDevice(){
    await cognito.forceForgotDevice();
  }

  async function checkIfUserHasMFA(){
    try{
      await cognito.checkIfUserHasMFA();
    }catch(err){
      signOut();
    }
  }

  async function signInFromIdpTokensSAML(tokens:any){
    try {
      let sub = JSON.parse(atob(tokens.id_token.split('.')[1]))["cognito:username"];

      let currUser = await cognito.setCurrentUserForAuthSAML(sub, tokens);

      /* const session: any = await getSession();
      console.log(session); */
      let date = new Date();
      // add a day
      let nextDay:number | Date | string = date.setDate(date.getDate() + 1);
      //set to midnight
      nextDay = date.setHours(0,0,0);

      nextDay = new Date(nextDay).toUTCString();

      document.cookie = `hubRefreshToken${config.ENV}=${tokens.refresh_token}; expires=${nextDay}; domain=${config.APPLICATION_DOMAIN}; path=/`;
      document.cookie = `hubAccessToken${config.ENV}=${tokens.access_token}; expires=${nextDay}; domain=${config.APPLICATION_DOMAIN}; path=/`;
      document.cookie = `hubIdToken${config.ENV}=${tokens.id_token}; expires=${nextDay}; domain=${config.APPLICATION_DOMAIN}; path=/`;

      //retrieve session
      const session:any = await getSession();

      setSessionInfo({
        accessToken: session.getAccessToken().getJwtToken(),
        refreshToken: session.getRefreshToken().getToken(),
        idToken: session.getIdToken().getJwtToken()
      });

      setAuthStatus(AuthStatus.SignedIn);

      return 'LOGGED'

    } catch (err) {
      //console.log('err email login', err);
      setAuthStatus(AuthStatus.SignedOut);
      throw err;
    }
  }

  const logoutInvalidateAllTokens = async () => {
    try{
      let session: any = await getSession();
      const encodeGetParams = (p:any) => 
          Object.entries(p).map(kv => kv.map(encodeURIComponent).join("=")).join("&");
      
      const params = {
        token:session.refreshToken.token,
        client_id:config.REACT_APP_CLIENT_ID,
      };

      const axiosParams = {
        url: '/oauth2/revoke',
        method: 'post',
        baseURL: config.COGNITO_DOMAIN_URL,
        headers: { 
            'Content-Type': 'application/x-www-form-urlencoded'
        },
        data: encodeGetParams(params)
      };

      const responseRevoke = await axios(axiosParams);

      cognito.signOut();
      localStorage.removeItem('hubRefreshToken');
      localStorage.removeItem('hubAccessToken');
      localStorage.removeItem('hubIdToken');
      document.cookie = `hubRefreshToken${config.ENV}=a; expires=Thu, 01 Jan 1970 00:00:00 UTC; domain=${config.APPLICATION_DOMAIN}; path=/`;
      document.cookie = `hubAccessToken${config.ENV}=a; expires=Thu, 01 Jan 1970 00:00:00 UTC; domain=${config.APPLICATION_DOMAIN}; path=/`;
      document.cookie = `hubIdToken${config.ENV}=a; expires=Thu, 01 Jan 1970 00:00:00 UTC; domain=${config.APPLICATION_DOMAIN}; path=/`;
      setAuthStatus(AuthStatus.SignedOut);
    }catch(e){
      console.log('', e);
    }
  };

  async function updateMissingTokensFromLoggedInSession(){
    let currentSession: any = await getSession();
    //console.log(currentSession)
    setSessionInfo({
      accessToken: currentSession.getAccessToken().getJwtToken(),
      refreshToken: currentSession.getRefreshToken().getToken(),
      idToken: currentSession.getIdToken().getJwtToken()
    });

    let date = new Date();
    // add a day
    let nextDay:number | Date | string = date.setDate(date.getDate() + 1);
    //set to midnight
    nextDay = date.setHours(0,0,0);

    nextDay = new Date(nextDay).toUTCString();
    document.cookie = `hubAccessToken${config.ENV}=${currentSession.getAccessToken().getJwtToken()}; expires=${nextDay}; domain=${config.APPLICATION_DOMAIN}; path=/`;
    document.cookie = `hubRefreshToken${config.ENV}=${currentSession.getRefreshToken().getToken()}; expires=${nextDay}; domain=${config.APPLICATION_DOMAIN}; path=/`;
    document.cookie = `hubIdToken${config.ENV}=${currentSession.getIdToken().getJwtToken()}; expires=${nextDay}; domain=${config.APPLICATION_DOMAIN}; path=/`;
  }

  const state: IAuth = {
    authStatus,
    sessionInfo,
    attrInfo,
    //signUpWithEmail,
    signInWithEmail,
    signOut,
    verifyCode,
    getSession,
    sendCode,
    forgotPassword,
    changePassword,
    getAttributes,
    setAttribute,
    refreshToken,
    requestTokenTOTP,
    verifySoftwareTOTP,
    enableMFA,
    signInWithTOTP,
    rememberDevice,
    getDevice,
    forceForgetDevice,
    checkIfUserHasMFA,
    signInFromIdpTokensSAML,
    logoutInvalidateAllTokens,
    getAccessTokenForBE,
    updateMissingTokensFromLoggedInSession
  }

  return <AuthContext.Provider value={state}>{children}</AuthContext.Provider>
}

export default AuthProvider
